import { useEffect, useRef, useState } from 'react'
import { MapPin, Navigation, X, Loader2 } from 'lucide-react'
import { useLocation as useUserLocation } from '../contexts/LocationContext'
import { useAuth } from '../hooks/useAuth'
import { useGoogleMaps } from '../hooks/useGoogleMaps'
import NativeApp from '../utils/nativeApp'

/**
 * LocationMismatchPrompt
 * ----------------------
 * Zomato-style "you're not at your saved address" sheet.
 *
 * Behaviour:
 *  - Runs **once per browser session** (sessionStorage key below).
 *  - Only fires when the user already has a saved delivery location AND a
 *    fresh GPS fix is available; otherwise it stays silent.
 *  - Compares the saved location to the GPS fix using a simple haversine
 *    distance. NOTE: this is *not* an ETA — the spec ban on client-side
 *    haversine is for delivery-ETA computation. Using it here as a binary
 *    "same place / different place" check is fine.
 *  - If the distance exceeds `THRESHOLD_METERS`, opens a non-blocking
 *    bottom sheet asking the user to either continue with the saved
 *    address or switch to their current location.
 *
 * Native parity: the native shells should mirror this UX on cold start —
 * see §11 of `NATIVE_INTEGRATION_GUIDE.md`.
 */

const SESSION_KEY = 'loc_mismatch_checked'
const THRESHOLD_METERS = 500 // ~5 city blocks. Anything bigger feels different.

const haversineMeters = (a, b) => {
  if (!a || !b) return 0
  const toRad = d => (d * Math.PI) / 180
  const R = 6371000 // metres
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * R * Math.asin(Math.sqrt(h))
}

const stripPlusCode = (addr = '') => addr.replace(/^[A-Z0-9]{4,6}\+[A-Z0-9]{2,4}\s*,?\s*/i, '').trim()

const LocationMismatchPrompt = () => {
  const { token } = useAuth()
  const { location: savedLocation, hasLocation, loading, updateLocation } = useUserLocation()
  const { isLoaded: mapsReady } = useGoogleMaps()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [detected, setDetected] = useState(null)
  const ranRef = useRef(false)

  useEffect(() => {
    if (ranRef.current) return
    if (!token) return
    if (typeof window !== 'undefined' && window.YumNativeMismatchHandled === true) {
      // Native shell renders its own bottom sheet — see §11 of NATIVE_INTEGRATION_GUIDE.md
      ranRef.current = true
      return
    }
    if (loading) return
    if (!hasLocation || !savedLocation) return
    if (!mapsReady) return // need Geocoder for the "detected address" label

    // Only run once per session — we don't want to nag the user on every
    // route change. If they explicitly want to switch later they can use
    // the location chip in the header.
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') {
        ranRef.current = true
        return
      }
    } catch {
      /* private mode — fall through */
    }

    ranRef.current = true

    let cancelled = false
    ;(async () => {
      try {
        const fix = await NativeApp.getCurrentLocation({ enableHighAccuracy: true, timeout: 8000 })
        if (cancelled) return

        const savedLat = Number(savedLocation.latitude)
        const savedLng = Number(savedLocation.longitude)
        if (!Number.isFinite(savedLat) || !Number.isFinite(savedLng)) return

        const distance = haversineMeters({ lat: savedLat, lng: savedLng }, { lat: fix.lat, lng: fix.lng })

        if (distance < THRESHOLD_METERS) {
          // Same area — saved location is still good. Mark as checked.
          try {
            sessionStorage.setItem(SESSION_KEY, '1')
          } catch {}
          return
        }

        // Reverse-geocode the GPS fix so the prompt shows a friendly
        // address instead of raw coords.
        let address = ''
        let city = ''
        let area_name = ''
        let place_id = ''
        try {
          const geocoder = new window.google.maps.Geocoder()
          const res = await geocoder.geocode({ location: { lat: fix.lat, lng: fix.lng } })
          const place = res?.results?.[0]
          if (place) {
            address = stripPlusCode(place.formatted_address) || place.name || ''
            const comps = place.address_components || []
            city = comps.find(c => c.types.includes('locality') || c.types.includes('administrative_area_level_2'))?.long_name || ''
            area_name = comps.find(c => c.types.includes('sublocality') || c.types.includes('neighborhood') || c.types.includes('sublocality_level_1'))?.long_name || ''
            place_id = place.place_id || ''
          }
        } catch {
          /* fall through with empty labels */
        }

        if (cancelled) return
        setDetected({
          lat: fix.lat,
          lng: fix.lng,
          accuracy: fix.accuracy,
          address: address || `${fix.lat.toFixed(5)}, ${fix.lng.toFixed(5)}`,
          city,
          area_name,
          place_id,
          distanceMeters: distance
        })
        setOpen(true)
      } catch {
        // Permission denied / timeout / native bridge unavailable — stay
        // silent. The user can still pick a location from the header.
        try {
          sessionStorage.setItem(SESSION_KEY, '1')
        } catch {}
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, loading, hasLocation, savedLocation, mapsReady])

  const closeAndRemember = () => {
    setOpen(false)
    try {
      sessionStorage.setItem(SESSION_KEY, '1')
    } catch {}
  }

  const keepSaved = () => {
    closeAndRemember()
  }

  const switchToCurrent = async () => {
    if (!detected) return
    setBusy(true)
    try {
      await updateLocation({
        latitude: detected.lat,
        longitude: detected.lng,
        address: detected.address,
        city: detected.city,
        area_name: detected.area_name,
        place_id: detected.place_id,
        source: 'gps'
      })
      closeAndRemember()
    } catch {
      // updateLocation already logs; just close so the user isn't stuck.
      closeAndRemember()
    } finally {
      setBusy(false)
    }
  }

  if (!open || !detected) return null

  const distanceLabel = detected.distanceMeters >= 1000 ? `${(detected.distanceMeters / 1000).toFixed(1)} km away` : `${Math.round(detected.distanceMeters)} m away`

  const savedLabel = (savedLocation?.address || savedLocation?.area_name || savedLocation?.city || 'Saved location').toString()

  return (
    <div className="lmp-backdrop lmp-backdrop--above-bottomnav" role="dialog" aria-modal="true" aria-labelledby="lmp-title">
      <div className="lmp-sheet" role="document">
        <button type="button" className="lmp-close" aria-label="Dismiss" onClick={keepSaved} disabled={busy}>
          <X size={18} />
        </button>

        <div className="lmp-header">
          <div className="lmp-icon-wrap">
            <Navigation size={22} />
          </div>
          <h3 id="lmp-title" className="lmp-title">
            You seem to be somewhere else
          </h3>
          <p className="lmp-sub">We noticed your current location is {distanceLabel} from your saved address. Where would you like to order to?</p>
        </div>

        <div className="lmp-options">
          <button type="button" className="lmp-option" onClick={keepSaved} disabled={busy}>
            <span className="lmp-option-icon" style={{ background: '#fff7ed', color: 'hsl(24, 100%, 49%)' }}>
              <MapPin size={18} />
            </span>
            <span className="lmp-option-body">
              <span className="lmp-option-label">Yes Continue With Saved Address</span>
              <span className="lmp-option-text">{savedLabel}</span>
            </span>
          </button>

          <button type="button" className="lmp-option lmp-option--primary" onClick={switchToCurrent} disabled={busy}>
            <span className="lmp-option-icon" style={{ background: 'hsl(148, 60%, 95%)', color: 'hsl(148, 59%, 39%)' }}>
              {busy ? <Loader2 size={18} className="lmp-spin" /> : <Navigation size={18} />}
            </span>
            <span className="lmp-option-body">
              <span className="lmp-option-label">Use my current location</span>
              <span className="lmp-option-text">{detected.address}</span>
            </span>
          </button>
        </div>
      </div>

      <style>{`
        .lmp-backdrop {
          position: fixed; inset: 0; z-index: 10000;
          background: rgba(15, 23, 42, 0.55);
          display: flex; align-items: flex-end; justify-content: center;
          animation: lmp-fade .18s ease-out;
          /* iOS WKWebView: avoid the URL/toolbar clipping the sheet */
          padding-top: env(safe-area-inset-top);
          /* Lift the sheet above the fixed bottom nav (≈52px tall + safe
             area inset) so the action buttons aren't hidden behind it. */
          padding-bottom: calc(56px + env(safe-area-inset-bottom));
          /* Use dynamic viewport units so the sheet sizes against the
             *visible* viewport on iOS (excludes the dynamic toolbar). */
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
        }
        .lmp-sheet {
          position: relative;
          width: 100%; max-width: 480px;
          background: #fff;
          border-radius: 20px 20px 0 0;
          padding: 22px 22px 26px;
          /* Reserve room for the iOS home-indicator so the bottom button
             never sits under the gesture bar. */
          padding-bottom: calc(26px + env(safe-area-inset-bottom));
          box-shadow: 0 -10px 40px rgba(15, 23, 42, 0.22);
          animation: lmp-rise .22s cubic-bezier(.2,.8,.2,1);
          /* If the content is taller than the viewport (small phones,
             landscape, large dynamic type) make the sheet itself scroll
             instead of pushing buttons off-screen. */
          max-height: 100dvh;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        @media (min-width: 640px) {
          .lmp-backdrop { align-items: center; padding: 16px; }
          .lmp-sheet {
            border-radius: 20px;
            max-height: calc(100dvh - 32px);
            padding-bottom: 26px;
          }
        }
        .lmp-close {
          position: absolute; top: 12px; right: 12px;
          width: 32px; height: 32px;
          border-radius: 50%;
          border: 1px solid #e5e7eb; background: #fff;
          color: #6b7280;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: background .15s ease, color .15s ease;
        }
        .lmp-close:hover { background: #f9fafb; color: #111827; }
        .lmp-close:disabled { opacity: .5; cursor: not-allowed; }

        .lmp-header { padding: 6px 6px 14px; }
        .lmp-icon-wrap {
          width: 44px; height: 44px;
          border-radius: 14px;
          background: hsl(24, 100%, 95%); color: hsl(24, 100%, 49%);
          display: inline-flex; align-items: center; justify-content: center;
          margin-bottom: 12px;
        }
        .lmp-title {
          margin: 0 0 6px;
          font-size: 18px; font-weight: 700;
          color: #111827; letter-spacing: -.01em;
        }
        .lmp-sub {
          margin: 0;
          font-size: 14px; color: #4b5563; line-height: 1.5;
        }

        .lmp-options { display: flex; flex-direction: column; gap: 10px; margin-top: 6px; }
        .lmp-option {
          display: flex; align-items: center; gap: 12px;
          width: 100%; padding: 14px;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #fff;
          text-align: left;
          cursor: pointer;
          transition: border-color .15s ease, background .15s ease, transform .12s ease;
        }
        .lmp-option:hover:not(:disabled) {
          border-color: hsl(24, 100%, 49%);
          background: #fffaf5;
        }
        .lmp-option:disabled { opacity: .65; cursor: not-allowed; }
        .lmp-option--primary { border-color: hsl(24, 100%, 49%); background: hsl(24, 100%, 99%); }

        .lmp-option-icon {
          width: 38px; height: 38px;
          border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .lmp-option-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .lmp-option-label { font-size: 14px; font-weight: 600; color: #111827; }
        .lmp-option-text {
          font-size: 13px; color: #6b7280;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          max-width: 320px;
        }

        .lmp-spin { animation: lmp-rotate 1s linear infinite; }
        @keyframes lmp-rotate { to { transform: rotate(360deg); } }
        @keyframes lmp-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lmp-rise {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

export default LocationMismatchPrompt
